// src/components/Layout/AppLayout.tsx
import React, { useState, useEffect } from 'react';
import { useUiStateStore } from '@stores/uiStateStore';
import { AuthModal } from '@/components/Auth/AuthModal';
import { TopToolbar } from './TopToolbar/TopToolbar';
import { LeftSidebar } from './LeftSidebar/LeftSidebar';
import { RightSidebar } from './RightSidebar/RightSidebar';
import { CanvasArea } from './CanvasArea/CanvasArea';
import { IconReplacementManager } from '../IconReplacement/IconReplacementManager';
import { SidebarDragPreview } from '../Sidebar/ComponentLibrary/SidebarDragPreview';
import { LeftToolbar } from './LeftToolbar';
import { EditorOnboarding } from '@/components/Onboarding';

const AppLayoutContent: React.FC = () => {
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // Get editorMode and itemControls from the store
  const editorMode = useUiStateStore((state) => state.editorMode);
  const itemControls = useUiStateStore((state) => state.itemControls);

  // Determine if we're in read-only mode (mobile view)
  const isReadOnly = editorMode !== 'EDITABLE';
  // Auto-close right sidebar when no item is selected
  useEffect(() => {
    // In read-only mode, never open the right sidebar
    if (isReadOnly) {
      setIsRightSidebarOpen(false);
      return;
    }

    if (itemControls === null) {
      setIsRightSidebarOpen(false);
    } else if (itemControls.type === 'ADD_ITEM') {
      // Don't auto-open right sidebar when just adding items
      // Keep current state (user can manually toggle if needed)
    } else {
      // Auto-open when an item is selected
      setIsRightSidebarOpen(true);
    }
  }, [itemControls, isReadOnly]);

  useEffect(() => {
    const onHelpShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping = target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (isTyping) return;
      if (event.key === '?') {
        event.preventDefault();
        setIsHelpOpen(true);
      } else if (event.key === 'Escape' && isHelpOpen) {
        event.preventDefault();
        setIsHelpOpen(false);
      }
    };
    window.addEventListener('keydown', onHelpShortcut);
    return () => window.removeEventListener('keydown', onHelpShortcut);
  }, [isHelpOpen]);

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-white dark:bg-slate-950">
      {/* Top Toolbar */}
      <TopToolbar
        onToggleLeftSidebar={() => !isReadOnly && setIsLeftSidebarOpen(!isLeftSidebarOpen)}
        onToggleRightSidebar={() => !isReadOnly && setIsRightSidebarOpen(!isRightSidebarOpen)}
        isLeftSidebarOpen={isLeftSidebarOpen}
        isRightSidebarOpen={isRightSidebarOpen}
        isReadOnly={isReadOnly}
        onOpenHelp={() => setIsHelpOpen(true)}
      />

      {/* Main Content Area */}
      <div className="relative flex flex-1 overflow-hidden">
        {/* Canvas Area - Takes full width (no sidebars in layout flow) */}
        <CanvasArea
          isLeftSidebarOpen={isLeftSidebarOpen}
          isRightSidebarOpen={isRightSidebarOpen}
        />

        {/* Left Toolbar - OVERLAY on canvas (positioned absolute) - Hidden in read-only mode */}
        {!isReadOnly && (
          <LeftToolbar
            isLeftSidebarOpen={isLeftSidebarOpen}
            onOpenLeftSidebar={() => setIsLeftSidebarOpen(true)}
          />
        )}

        {/* Left Sidebar - OVERLAY (positioned absolute) - Hidden in read-only mode */}
        {!isReadOnly && (
          <div
            data-editor-ui="true"
            className={`absolute left-0 top-0 h-full w-80 transform transition-transform duration-300 ease-in-out ${
              isLeftSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            style={{ zIndex: 10 }}
          >
            <LeftSidebar />
          </div>
        )}

        {/* Right Sidebar - OVERLAY (positioned absolute) - Hidden in read-only mode */}
        {!isReadOnly && (
          <div
            data-editor-ui="true"
            className={`absolute right-0 top-0 h-full w-80 transform transition-transform duration-300 ease-in-out ${
              isRightSidebarOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            style={{ zIndex: 10 }}
          >
            <RightSidebar />
          </div>
        )}

        <EditorOnboarding
          isHelpOpen={isHelpOpen}
          isReadOnly={isReadOnly}
          onCloseHelp={() => setIsHelpOpen(false)}
          onOpenHelp={() => setIsHelpOpen(true)}
          onOpenLibrary={() => setIsLeftSidebarOpen(true)}
        />
      </div>
      <IconReplacementManager />
      <SidebarDragPreview />
      <AuthModal />
    </div>
  );
};

export const AppLayout: React.FC = () => {
  return <AppLayoutContent />;
};
